import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Loader2, LogOut } from "lucide-react";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

const inputCls =
  "h-14 w-full border border-white/10 bg-coal px-5 font-mono text-xs tracking-wider text-zinc-100 placeholder:text-zinc-600 transition-colors duration-300 focus:border-white/50 focus:outline-none";

export default function Auth() {
  const { user, login, register, logout, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      if (tab === "login") {
        await login(form.email, form.password);
        toast.success("Bon retour.");
      } else {
        await register(form.name, form.email, form.password);
        toast.success("Bienvenue dans la maison.");
      }
      navigate("/");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    await logout();
    toast.success("Déconnecté.");
  };

  return (
    <div className="mx-auto flex min-h-screen max-w-[1700px] items-center px-5 pb-20 pt-36 sm:px-8 lg:px-16" data-testid="auth-page">
      <div className="mx-auto w-full max-w-md">
        <Reveal>
          <p className="text-center font-mono text-[10px] uppercase tracking-[0.35em] text-zinc-500">
            Espace Privé
          </p>
          <h1 className="mt-4 text-center font-blackletter text-5xl leading-[1] sm:text-6xl">
            Le Cercle
          </h1>
        </Reveal>

        {user ? (
          <Reveal delay={0.1}>
            <div className="mt-10 border border-white/10 bg-coal p-8 text-center" data-testid="account-card">
              {user.picture && (
                <img src={user.picture} alt="" className="mx-auto h-16 w-16 rounded-full border border-white/10" />
              )}
              <p className="mt-4 font-display text-lg font-bold uppercase tracking-wide">{user.name}</p>
              <p className="mt-1 font-mono text-xs text-zinc-500">{user.email}</p>
              {user.role === "admin" && (
                <p className="mt-2 inline-block border border-white/20 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.3em]">
                  Admin
                </p>
              )}
              <button
                onClick={signOut}
                data-testid="logout-button"
                className="mt-8 flex h-12 w-full items-center justify-center gap-2 border border-white/15 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-300 transition-colors duration-300 hover:border-white hover:text-white"
              >
                <LogOut className="h-3.5 w-3.5" /> Se déconnecter
              </button>
            </div>
          </Reveal>
        ) : (
          <Reveal delay={0.1}>
            <div className="mt-10 border border-white/10 bg-coal p-7 sm:p-9">
              <div className="grid grid-cols-2 border border-white/10">
                {["login", "register"].map((t) => (
                  <button
                    key={t}
                    data-testid={t === "login" ? "tab-login" : "tab-register"}
                    onClick={() => {
                      setTab(t);
                      setError("");
                    }}
                    className={`h-12 font-mono text-[10px] uppercase tracking-[0.25em] transition-colors duration-300 ${
                      tab === t ? "bg-white text-ink" : "text-zinc-500 hover:text-white"
                    }`}
                  >
                    {t === "login" ? "Connexion" : "Inscription"}
                  </button>
                ))}
              </div>

              <form onSubmit={submit} className="mt-7 space-y-4" data-testid="auth-form">
                {tab === "register" && (
                  <input
                    required
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Nom"
                    data-testid="auth-name-input"
                    className={inputCls}
                  />
                )}
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="Email"
                  data-testid="auth-email-input"
                  className={inputCls}
                />
                <input
                  required
                  type="password"
                  minLength={8}
                  value={form.password}
                  onChange={set("password")}
                  placeholder="Mot de passe (8+ caractères)"
                  data-testid="auth-password-input"
                  className={inputCls}
                />

                {error && (
                  <p data-testid="auth-error" className="border border-white/20 bg-ink px-4 py-3 font-mono text-[11px] tracking-wider text-zinc-200">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="auth-submit-button"
                  className="group flex h-14 w-full items-center justify-center gap-3 bg-white font-mono text-[11px] uppercase tracking-[0.3em] text-ink transition-colors duration-300 hover:bg-zinc-300 disabled:opacity-60"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      {tab === "login" ? "Entrer" : "Créer mon compte"}
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </>
                  )}
                </button>
              </form>

              <div className="my-7 flex items-center gap-4">
                <span className="h-px flex-1 bg-white/10" />
                <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-zinc-600">Ou</span>
                <span className="h-px flex-1 bg-white/10" />
              </div>

              <button
                onClick={loginWithGoogle}
                data-testid="google-signin-button"
                className="flex h-14 w-full items-center justify-center gap-3 border border-white/15 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-100 transition-colors duration-300 hover:border-white"
              >
                <span className="font-display text-base font-bold">G</span> Continuer avec Google
              </button>
            </div>
          </Reveal>
        )}
      </div>
    </div>
  );
}
